import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashGroupBy from 'lodash/fp/groupBy';

import {
  PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
  PROPERTY_ATTRIBUTE_CATEGORY_FEATURE,
  PROPERTY_ATTRIBUTE_LEVEL_FLOOR_PLAN,
} from 'common/propertyAttributeConstants';

import { LISTING_FORM_TYPE_FLOOR_PLAN_PROPERTY_ATTRIBUTES } from 'common/listingFormFields';

import { LISTING_STATUS_ACTIVE } from 'common/listingConstants';

import { getSquareFeetRangeDisplay } from 'common/listingHelpers';

import { SR_API_ACTION_FLOOR_PLAN_DELETE, SR_API_ACTION_FLOOR_PLAN_UPSERT } from 'api/actionTypes';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrTable from 'client/components/SrTable';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrFloorPlanModal from 'client/components/SrFloorPlanModal';
import SrAdminMediaListTable from 'client/components/SrAdminMediaListTable';
import SrDataTableMedia from 'client/components/SrDataTableMedia';
import SrAdminPhotosEditor, {
  SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_FLOOR_PLAN,
} from 'client/components/SrAdminPhotosEditor';
import SrPropertyAttributeFeatureModal from 'client/components/SrPropertyAttributeFeatureModal';
import SrPropertyAttributeListingFieldFormModal from 'client/components/SrPropertyAttributeListingFieldFormModal';

import postWithState from 'client/actions/postWithState';

class SrTableFloorPlans extends Component {
  static propTypes = {
    apartmentId: PropTypes.number.isRequired,
    floorPlans: PropTypes.array.isRequired,
    listings: PropTypes.array.isRequired,
    propertyAttributes: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      floorPlanModalProps: null,
      floorPlanModalPhotosProps: null,
      editMediaModalFloorPlan: null,
      propertyAttributeFeatureModalProps: null,
      propertyAttributeListModalProps: null,
    };
  }

  _onDeleteFloorPlan = (e) => {
    const { floorPlans, dispatch, onChange } = this.props;
    const floorPlanId = parseInt(e.target.dataset.id, 10);
    const floorPlan = floorPlans.find(({ id }) => id === floorPlanId);

    if (
      !global.confirm(`Are you sure you want to delete the floor plan "${floorPlan.displayName}"?`)
    ) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_FLOOR_PLAN_DELETE,
      payload: {
        floorPlanId,
      },
      onSuccess: onChange,
      onError: (err) => {
        global.alert(`Error: "${err.message}"`);
      },
    });
  };

  _onChangeFloorPlan = () => {
    this._onCloseFloorPlanModal();
    this.props.onChange();
  };

  _onClickEditFloorPlan = (e) => {
    const { floorPlans, propertyAttributes } = this.props;
    const floorPlanId = parseInt(e.target.dataset.id, 10);
    this.setState({
      floorPlanModalProps: {
        floorPlan: floorPlans.find(({ id }) => id === floorPlanId),
        propertyAttributes: propertyAttributes.filter(
          (propertyAttribute) =>
            propertyAttribute.category === PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD &&
            propertyAttribute.floorPlanId === floorPlanId,
        ),
      },
    });
  };

  _onCloseFloorPlanModal = () => {
    this.setState({
      floorPlanModalProps: null,
    });
  };

  _onClickOpenFloorPlanPhotos = (e) => {
    const { floorPlans } = this.props;
    const floorPlanId = parseInt(e.target.dataset.id, 10);
    this.setState({
      floorPlanModalPhotosProps: {
        floorPlan: floorPlans.find(({ id }) => id === floorPlanId),
      },
    });
  };

  _onCloseFloorPlanPhotosModal = () => {
    this.setState({
      floorPlanModalPhotosProps: null,
    });
  };

  _onSaveFloorPlanPhotos = (photoUrls) => {
    const {
      floorPlanModalPhotosProps: { floorPlan },
    } = this.state;
    const { dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_FLOOR_PLAN_UPSERT,
      payload: {
        ...floorPlan,
        photoUrls,
      },
      onSuccess: () => {
        this._onCloseFloorPlanPhotosModal();
        this.props.onChange();
      },
    });
  };

  _onClickEditFloorPlanPropertyAttributeList = (e) => {
    const { floorPlans } = this.props;
    const floorPlanId = parseInt(e.target.dataset.id, 10);
    this.setState({
      propertyAttributeListModalProps: {
        floorPlan: floorPlans.find(({ id }) => id === floorPlanId),
      },
    });
  };

  _onClickEditFloorPlanMedia = (floorPlan) => {
    this.setState({
      editMediaModalFloorPlan: floorPlan,
    });
  };

  _onCloseEditFloorPlanMediaModal = () => {
    this.setState({
      editMediaModalFloorPlan: null,
    });
  };

  _onClosePropertyAttributeListModal = () => {
    this.setState({
      propertyAttributeListModalProps: null,
    });
  };

  _onChangePropertyAttributes = () => {
    this._onClosePropertyAttributeListModal();
    this.props.onChange();
  };

  _onClickEditPropertyAttributeFeature = (e) => {
    const floorPlanId = parseInt(e.target.dataset.id, 10);
    this.setState({
      propertyAttributeFeatureModalProps: {
        floorPlanId,
      },
    });
  };

  _onClosePropertyAttributeFeatureModal = () => {
    this.setState({
      propertyAttributeFeatureModalProps: null,
    });
  };

  _onChangePropertyAttributeFeature = () => {
    this._onClosePropertyAttributeFeatureModal();
    this.props.onChange();
  };

  render() {
    const {
      floorPlanModalProps,
      floorPlanModalPhotosProps,
      propertyAttributeFeatureModalProps,
      propertyAttributeListModalProps,
      editMediaModalFloorPlan,
    } = this.state;
    const { apartmentId, floorPlans, listings, propertyAttributes, dispatch } = this.props;
    const listingsByFloorPlanId = lodashGroupBy('floorPlanId', listings);

    return (
      <SrBox>
        <SrTable
          headers={[
            { content: 'id' },
            { content: 'display name' },
            { content: 'description' },
            { content: 'SqFt' },
            { content: 'units' },
            { content: 'active units' },
            { content: 'show units' },
            { content: 'photos' },
            { content: 'features' },
            { content: 'attributes' },
            { content: 'media' },
            { content: 'options' },
          ]}
          rows={floorPlans.map((floorPlan) => ({
            cells: [
              { content: floorPlan.id },
              { content: floorPlan.displayName },
              { content: floorPlan.description },
              {
                content: (
                  <SrBox>
                    {getSquareFeetRangeDisplay(listingsByFloorPlanId[floorPlan.id] || [])}
                  </SrBox>
                ),
              },
              {
                content: (
                  <SrBox>
                    {(listingsByFloorPlanId[floorPlan.id] || []).map((listing) => (
                      <SrBox display="inlineBlock" key={listing.id} marginRight={4}>
                        <SrLink
                          color="blue300"
                          target="_blank"
                          to={`/admin/listings/edit/${listing.id}`}
                        >
                          {listing.apartmentUnit}
                        </SrLink>
                      </SrBox>
                    ))}
                  </SrBox>
                ),
              },
              {
                content: (
                  <SrBox>
                    {(listingsByFloorPlanId[floorPlan.id] || [])
                      .filter((listing) => listing.status === LISTING_STATUS_ACTIVE)
                      .map((listing) => (
                        <SrBox display="inlineBlock" key={listing.id} marginRight={4}>
                          <SrLink
                            color="blue300"
                            target="_blank"
                            to={`/admin/listings/edit/${listing.id}`}
                          >
                            {listing.apartmentUnit}
                          </SrLink>
                        </SrBox>
                      ))}
                  </SrBox>
                ),
              },
              {
                style:
                  (listingsByFloorPlanId[floorPlan.id] || []).filter(
                    (listing) => listing.status === LISTING_STATUS_ACTIVE,
                  ).length > 0 && floorPlan.tourableApartmentUnits.length === 0
                    ? { backgroundColor: '#E7711B', color: 'white' }
                    : {},
                content: (
                  <SrBox>
                    {(listingsByFloorPlanId[floorPlan.id] || [])
                      .filter((listing) =>
                        floorPlan.tourableApartmentUnits.includes(listing.apartmentUnit),
                      )
                      .map((listing) => (
                        <SrBox display="inlineBlock" key={listing.id} marginRight={4}>
                          <SrLink
                            color="blue300"
                            target="_blank"
                            to={`/admin/listings/edit/${listing.id}`}
                          >
                            {listing.apartmentUnit}
                          </SrLink>
                        </SrBox>
                      ))}
                  </SrBox>
                ),
              },
              {
                content: (
                  <div>
                    <SrText
                      color="blue300"
                      cursor="pointer"
                      data-id={floorPlan.id}
                      onClick={this._onClickOpenFloorPlanPhotos}
                    >
                      Edit Photos
                    </SrText>
                  </div>
                ),
              },
              {
                content: (
                  <SrText
                    cursor="pointer"
                    color="blue300"
                    data-id={floorPlan.id}
                    onClick={this._onClickEditPropertyAttributeFeature}
                  >
                    Edit Features
                  </SrText>
                ),
              },
              {
                content: (
                  <SrText
                    cursor="pointer"
                    color="blue300"
                    data-id={floorPlan.id}
                    onClick={this._onClickEditFloorPlanPropertyAttributeList}
                  >
                    Edit Attributes
                  </SrText>
                ),
              },
              {
                content: (
                  <SrText
                    cursor="pointer"
                    color="blue300"
                    data-id={floorPlan.id}
                    onClick={() => this._onClickEditFloorPlanMedia(floorPlan)}
                  >
                    Edit Media
                  </SrText>
                ),
              },
              {
                content: (
                  <div>
                    <SrText
                      cursor="pointer"
                      color="blue300"
                      data-id={floorPlan.id}
                      onClick={this._onClickEditFloorPlan}
                    >
                      Edit
                    </SrText>
                    <SrBox display="inlineBlock" width={10} />
                    <SrText
                      cursor="pointer"
                      color="blue300"
                      data-id={floorPlan.id}
                      onClick={this._onDeleteFloorPlan}
                    >
                      Delete
                    </SrText>
                  </div>
                ),
              },
            ],
          }))}
        />
        {floorPlanModalProps && (
          <SrFloorPlanModal
            {...floorPlanModalProps}
            onChange={this._onChangeFloorPlan}
            onClose={this._onCloseFloorPlanModal}
            dispatch={dispatch}
          />
        )}
        {floorPlanModalPhotosProps && (
          <SrDarkModal onClose={this._onCloseFloorPlanPhotosModal}>
            <SrModalContent style={{ width: 1040, height: '94vh', marginTop: 4 }}>
              <SrAdminPhotosEditor
                filePath={SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_FLOOR_PLAN}
                photoUrls={floorPlanModalPhotosProps.floorPlan.photoUrls}
                onSave={this._onSaveFloorPlanPhotos}
              />
            </SrModalContent>
          </SrDarkModal>
        )}
        {propertyAttributeListModalProps && (
          <SrPropertyAttributeListingFieldFormModal
            title={`Floor Plan "${propertyAttributeListModalProps.floorPlan.displayName}"`}
            formType={LISTING_FORM_TYPE_FLOOR_PLAN_PROPERTY_ATTRIBUTES}
            propertyAttributes={propertyAttributes.filter(
              ({ category, floorPlanId }) =>
                category === PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD &&
                floorPlanId === propertyAttributeListModalProps.floorPlan.id,
            )}
            apartmentId={propertyAttributeListModalProps.floorPlan.apartmentId}
            floorPlanId={propertyAttributeListModalProps.floorPlan.id}
            onChange={this._onChangePropertyAttributes}
            onClose={this._onClosePropertyAttributeListModal}
            dispatch={dispatch}
          />
        )}
        {propertyAttributeFeatureModalProps && (
          <SrPropertyAttributeFeatureModal
            propertyAttributes={propertyAttributes.filter(
              ({ category, floorPlanId }) =>
                category === PROPERTY_ATTRIBUTE_CATEGORY_FEATURE &&
                floorPlanId === propertyAttributeFeatureModalProps.floorPlanId,
            )}
            apartmentId={apartmentId}
            floorPlanId={propertyAttributeFeatureModalProps.floorPlanId}
            propertyAttributeLevel={PROPERTY_ATTRIBUTE_LEVEL_FLOOR_PLAN}
            onClose={this._onClosePropertyAttributeFeatureModal}
            onChange={this._onChangePropertyAttributeFeature}
            dispatch={dispatch}
          />
        )}
        {editMediaModalFloorPlan && (
          <SrDarkModal onClose={this._onCloseEditFloorPlanMediaModal}>
            <SrModalContent>
              <SrText size="28" weight="600" paddingBottom="10x" display="block">
                {`Media for Floorplan ${editMediaModalFloorPlan.displayName}`}
              </SrText>
              <SrDataTableMedia floorPlanId={editMediaModalFloorPlan.id}>
                {({ mediaList, reloadMediaList, reloadMediaById }) => (
                  <SrAdminMediaListTable
                    mediaList={mediaList}
                    reloadMediaList={reloadMediaList}
                    reloadMediaById={reloadMediaById}
                    dispatch={dispatch}
                    createMediaDefaultValues={{
                      floorPlanId: editMediaModalFloorPlan.id,
                    }}
                    createMediaTitle={`Media for Floorplan ${editMediaModalFloorPlan.displayName}`}
                  />
                )}
              </SrDataTableMedia>
            </SrModalContent>
          </SrDarkModal>
        )}
      </SrBox>
    );
  }
}

SrTableFloorPlans.displayName = 'SrTableFloorPlans';

export default SrTableFloorPlans;
